import React, { useState, useEffect, useRef } from 'react';

const PDFIframePreview = ({ file, pageSelection, selectedPages = [], onPageToggle, orientation = 'portrait', scale = 90, color = 'bw' }) => {
  const [dimensions, setDimensions] = useState({ width: 260, height: 346 });
  const containerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth || 260,
          height: containerRef.current.clientHeight || 346
        });
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fileUrl = file?.viewLink || file?.url;

  if (!fileUrl) {
    return (
      <div className="pdf-error" style={{ color: '#ef4444', padding: '20px', fontSize: '13px', textAlign: 'center' }}>
        ⚠️ No preview available
      </div>
    );
  }

  // Use Google Docs Viewer to bypass X-Frame-Options restrictions
  const src = `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(fileUrl)}`;

  const isPpt = file?.name?.toLowerCase().endsWith('.pptx') || 
                file?.name?.toLowerCase().endsWith('.ppt') || 
                file?.mimeType?.includes('powerpoint') || 
                file?.mimeType?.includes('presentation');

  const isLandscape = orientation === 'landscape' || isPpt;
  const availableWidth = Math.max(120, dimensions.width - 24);
  const w = availableWidth;
  
  // Standard A4 aspect ratio is 1.414. PPT is 4:3 (0.75 ratio).
  const aspectRatio = isPpt ? 0.75 : (isLandscape ? 1 / 1.414 : 1.414);
  const h = availableWidth * aspectRatio;

  // Scale factor to map base dimensions (Portrait A4: 800x1131, Landscape A4: 1131x800, PPT: 1024x768)
  const baseWidth = isPpt ? 1024 : (isLandscape ? 1131 : 800);
  const baseHeight = isPpt ? 768 : (isLandscape ? 800 : 1131);
  const scaleFactor = (w / baseWidth) * (scale / 100);

  const containerStyle = {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflowY: 'auto',
    background: '#111120',
    padding: '16px 8px',
    boxSizing: 'border-box',
  };

  const numPages = file.pageCount || 1;
  const pagesArray = Array.from({ length: numPages }, (_, i) => i + 1);

  const isCustomMode = pageSelection === 'custom';
  const isRangeMode = pageSelection === 'range';
  const isInteractiveMode = isCustomMode || isRangeMode;

  return (
    <div ref={containerRef} className="pdf-iframe-container" style={containerStyle}>
      {pagesArray.map((pageNum) => {
        const isSelected = pageSelection === 'all' || selectedPages.includes(pageNum);
        const showUnselectedDim = isInteractiveMode && !isSelected;

        const sheetStyle = {
          background: '#ffffff',
          boxShadow: isSelected ? '0 8px 24px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0, 0, 0, 0.25)',
          borderRadius: '2px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          boxSizing: 'border-box',
          width: `${w}px`,
          height: `${h}px`,
          margin: '0 auto 20px auto',
          cursor: isCustomMode ? 'pointer' : 'default',
          opacity: showUnselectedDim ? 0.35 : 1,
          filter: showUnselectedDim ? 'grayscale(60%)' : 'none',
          transition: 'all 0.25s ease',
          transform: isCustomMode && isSelected ? 'scale(1.01)' : 'none',
          flexShrink: 0
        };

        const virtualSheetStyle = {
          width: `${baseWidth}px`,
          height: `${baseHeight}px`,
          position: 'absolute',
          top: '50%',
          left: '50%',
          overflow: 'hidden',
          transform: (isLandscape && !isPpt)
            ? `translate(-50%, -50%) rotate(90deg) scale(${scaleFactor})` 
            : `translate(-50%, -50%) scale(${scaleFactor})`,
          transformOrigin: 'center',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          background: '#ffffff',
          flexShrink: 0
        };

        const iframeStyle = {
          position: 'absolute',
          width: `${baseWidth}px`,
          height: `${baseHeight * numPages + 48}px`, // Make iframe height proportional to pageCount
          top: `${-48 - (pageNum - 1) * baseHeight}px`, // Shift up to clip out other pages
          left: '0',
          border: 'none',
          background: '#ffffff',
          filter: color === 'bw' ? 'grayscale(100%) contrast(120%)' : 'none',
          transition: 'filter 0.2s',
        };

        return (
          <div
            key={pageNum}
            className={`a4-sheet-container ${isLandscape ? 'landscape' : 'portrait'}`}
            style={sheetStyle}
            onClick={() => {
              if (isCustomMode) {
                onPageToggle?.(pageNum);
              }
            }}
          >
            <div style={virtualSheetStyle}>
              <iframe
                src={src}
                style={iframeStyle}
                title={`Document Preview Page ${pageNum}`}
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                scrolling="no"
              />
            </div>

            {/* Page Selector Pill (identical to PDFViewer style) */}
            <div
              style={{
                position: 'absolute',
                bottom: '12px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(242, 242, 247, 0.92)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                padding: '6px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.25)',
                zIndex: 2,
                pointerEvents: 'none',
                border: '0.5px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.2s ease'
              }}
            >
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  background: isSelected ? '#007aff' : 'rgba(0, 0, 0, 0.1)',
                  border: isSelected ? 'none' : '1.5px solid rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '9.5px',
                  fontWeight: '900',
                  transition: 'all 0.2s ease'
                }}
              >
                {isSelected ? '✓' : ''}
              </div>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#1c1c1e',
                  letterSpacing: '-0.1px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}
              >
                Page {pageNum} of {numPages}
              </span>
            </div>
          </div>
        );
      })}
      {fileUrl && (
        <div style={{ textAlign: 'center', padding: '8px 0 16px 0', width: '100%' }}>
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--primary-light, #6C63FF)', fontSize: '12px', textDecoration: 'none', fontWeight: '600' }}
          >
            ↗ View original document in new tab
          </a>
        </div>
      )}
    </div>
  );
};

export default PDFIframePreview;
